import clsx from "clsx";
import dayjs from "dayjs";
import { useMemo } from "react";

import { useDriverLocations } from "@web-app/api";
import { Icon, Tooltip } from "@web-app/components";
import useDispatchStore from "@web-app/store/dispatchStore";
import { Order, OrderDeliveryType } from "@web-app/types";
import { getOrderCompletionTime } from "@web-app/utils/helpers";

import ETAWithLabel from "../ETAWithLabel";

function DriverLocationUnavailableTooltip() {
  return (
    <Tooltip
      text={
        <span>
          Driver location is unavailable, so the live ETA can&apos;t be
          calculated. Showing the scheduled time.
        </span>
      }
      position="top"
      icon="fasCircleExclamation"
      iconClassName="!h-3 pl-1 text-red-500"
      tooltipClassName="min-w-[12rem] max-w-xs whitespace-normal break-words"
    />
  );
}

export default function OrderETAFooter({
  order,
  orderMayBeDelayed = false,
  isEarlyDeliveryOrder = false,
}: {
  isEarlyDeliveryOrder?: boolean;
  order: Order;
  orderMayBeDelayed: boolean;
}) {
  const { data } = useDriverLocations();
  const { routeModalMode } = useDispatchStore();

  const driverLocations = data?.driverLocations;

  const shouldShowOldEtaMessage =
    !!order.assigneeId &&
    !!driverLocations &&
    !driverLocations[order.assigneeId];

  const shouldShowEstimatedEta =
    !!order.estimatedEtaDateTime &&
    ((order.type === OrderDeliveryType.ROUTE_OPT_PICKUP && !order.pickupEta) ||
      (order.type !== OrderDeliveryType.ROUTE_OPT_PICKUP && !order.eta) ||
      !order.assigneeId ||
      routeModalMode === "edit");

  // TODO: This is added to handle old relish orders. Remove this logic after May
  const shouldShowEstimatedEtaForOldIndividualOrders =
    order.type === OrderDeliveryType.INDIVIDUAL_PICKUP_AND_DROPOFF &&
    !order.estimatedEtaDateTime &&
    !!order.eta?.dateTime;

  const showPickupEta =
    (order.type === OrderDeliveryType.ROUTE_OPT_PICKUP ||
      (order.type === OrderDeliveryType.INDIVIDUAL_PICKUP_AND_DROPOFF &&
        ![
          "In Transit to Drop-off",
          "Completed",
          "Cancelled",
          "Failed",
        ].includes(order.statusText))) &&
    !!order.pickupEta?.dateTime;

  const lastETAUpdateTimeStamp =
    order.type === OrderDeliveryType.INDIVIDUAL_PICKUP_AND_DROPOFF ||
    order.type === OrderDeliveryType.ROUTE_OPT_DROPOFF
      ? order.eta?.lastUpdated
      : order.pickupEta?.lastUpdated;

  const { isOrderDelayed, delayInMinutes, lastEtaCalculatedDiff } =
    useMemo(() => {
      let isOrderDelayed = false;
      let delayInMinutes = 0;
      let lastEtaCalculatedDiff = 0;

      if (!showPickupEta && !!order.eta?.dateTime) {
        isOrderDelayed =
          new Date(order.eta.dateTime!) > new Date(order.scheduledEndDateTime);
        if (isOrderDelayed) {
          delayInMinutes = dayjs(order.eta.dateTime).diff(
            dayjs(order.scheduledEndDateTime),
            "minute",
          );
        }
      } else if (showPickupEta) {
        if (order.type === OrderDeliveryType.INDIVIDUAL_PICKUP_AND_DROPOFF) {
          if (!order.pickupEta?.dateTime) {
            return { delayInMinutes, isOrderDelayed, lastEtaCalculatedDiff };
          }

          isOrderDelayed =
            new Date(order.pickupEta?.dateTime) >
            new Date(order.scheduledStartDateTime);

          if (isOrderDelayed) {
            delayInMinutes = dayjs(order.pickupEta?.dateTime).diff(
              dayjs(order.scheduledStartDateTime),
              "minute",
            );
          }
        } else {
          if (!order.pickupEta?.dateTime) {
            return { delayInMinutes, isOrderDelayed, lastEtaCalculatedDiff };
          }

          isOrderDelayed =
            new Date(order.pickupEta?.dateTime) >
            new Date(order.scheduledEndDateTime);

          if (isOrderDelayed) {
            delayInMinutes = dayjs(order.pickupEta?.dateTime).diff(
              dayjs(order.scheduledEndDateTime),
              "minute",
            );
          }
        }
      }

      if (delayInMinutes < 1) {
        isOrderDelayed = false;
      }

      if (
        order.type === OrderDeliveryType.ROUTE_OPT_PICKUP &&
        !!order.pickupEta?.lastUpdated
      ) {
        lastEtaCalculatedDiff = dayjs().diff(
          dayjs(order.pickupEta.lastUpdated),
          "minute",
        );
      } else if (order.eta?.lastUpdated) {
        lastEtaCalculatedDiff = dayjs().diff(
          dayjs(order.eta.lastUpdated),
          "minute",
        );
      }

      return { delayInMinutes, isOrderDelayed, lastEtaCalculatedDiff };
    }, [order, showPickupEta]);

  const orderCompletionTime = getOrderCompletionTime(order);

  return orderCompletionTime ? (
    <div className="text-center relative">
      <ETAWithLabel
        label={order.status === "CANCELLED" ? "Cancelled at " : "Completed at "}
        date={new Date(orderCompletionTime)}
        timezone={order.timezone}
      />
    </div>
  ) : (order.eta?.dateTime && order.eta?.originalDistance !== 0) ||
    (order.pickupEta?.dateTime && order.pickupEta.originalDistance !== 0) ? (
    <div className="flex items-center gap-1">
      <ETAWithLabel
        label={showPickupEta ? "Pickup ETA " : "Drop-off ETA "}
        date={
          showPickupEta
            ? new Date(order.pickupEta?.dateTime ?? "")
            : new Date(order.eta.dateTime ?? "")
        }
        timezone={order.timezone}
      />
      {order.type === OrderDeliveryType.INDIVIDUAL_PICKUP_AND_DROPOFF && (
        <span>
          <Tooltip
            text={
              <span>
                {order.actualPickupDateTime ? (
                  <>
                    Picked up at{" "}
                    {dayjs(order.actualPickupDateTime).format("HH:mm")}
                  </>
                ) : order.pickupEta?.dateTime ? (
                  <ETAWithLabel
                    label="Pickup ETA "
                    date={new Date(order.pickupEta?.dateTime ?? "")}
                    timezone={order.timezone}
                  />
                ) : (
                  "Pickup ETA unavailable."
                )}
                <br />
                {order.eta?.dateTime ? (
                  <ETAWithLabel
                    label="Drop-off ETA "
                    date={new Date(order.eta?.dateTime ?? "")}
                    timezone={order.timezone}
                  />
                ) : (
                  "Drop-off ETA unavailable"
                )}
                <br />

                {isOrderDelayed && (
                  <span>Delayed by {delayInMinutes} minutes. </span>
                )}
                {shouldShowOldEtaMessage && (
                  <>
                    <span>
                      Driver location last updated {lastEtaCalculatedDiff}{" "}
                      minutes ago.
                    </span>
                    <div>
                      <ETAWithLabel
                        label="ETA last updated at "
                        date={new Date(lastETAUpdateTimeStamp!)}
                        timezone={order.timezone}
                      />
                    </div>
                  </>
                )}
              </span>
            }
            position="top"
            icon={
              isOrderDelayed || shouldShowOldEtaMessage
                ? "fasCircleExclamation"
                : "circleExclamation"
            }
            iconClassName={clsx(
              "!h-3 pl-1",
              isOrderDelayed || shouldShowOldEtaMessage
                ? "text-red-500"
                : "text-gray-900",
            )}
            tooltipClassName="min-w-[12rem] max-w-xs whitespace-normal break-words"
          />
        </span>
      )}
      {(shouldShowOldEtaMessage || isOrderDelayed) &&
        order.type !== OrderDeliveryType.INDIVIDUAL_PICKUP_AND_DROPOFF && (
          <span>
            <Tooltip
              text={
                <>
                  {isOrderDelayed && (
                    <span>Delayed by {delayInMinutes} minutes. </span>
                  )}
                  {shouldShowOldEtaMessage && (
                    <>
                      <span>
                        Driver location has not been updated for over{" "}
                        {lastEtaCalculatedDiff} minutes.
                      </span>
                      <div>
                        <ETAWithLabel
                          label="ETA last updated at "
                          date={new Date(lastETAUpdateTimeStamp!)}
                          timezone={order.timezone}
                        />
                      </div>
                    </>
                  )}
                </>
              }
              position="top"
              icon="fasCircleExclamation"
              iconClassName={clsx("!h-3 pl-1 text-red-500")}
              tooltipClassName="min-w-[12rem] max-w-xs whitespace-normal break-words"
            />
          </span>
        )}
    </div>
  ) : shouldShowEstimatedEta ? (
    <div
      className={clsx(
        orderMayBeDelayed && "text-red-500",
        isEarlyDeliveryOrder && "text-blue-500",
        "flex items-center",
      )}
    >
      <span className={clsx("mr-1", !isEarlyDeliveryOrder && "invisible")}>
        <Icon name="chevronsUp" />
      </span>{" "}
      <ETAWithLabel
        label="ETA "
        date={new Date(order.estimatedEtaDateTime ?? "")}
        timezone={order.timezone}
      />
      {shouldShowOldEtaMessage && routeModalMode !== "edit" && (
        <DriverLocationUnavailableTooltip />
      )}
    </div>
  ) : shouldShowEstimatedEtaForOldIndividualOrders ? (
    <div className={clsx(orderMayBeDelayed && "text-red-500")}>
      <ETAWithLabel
        label="ETA "
        date={new Date(order.eta.dateTime ?? "")}
        timezone={order.timezone}
      />
    </div>
  ) : (
    <></>
  );
}
